import React from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { UserDto } from '@poker-stats/contracts';
import { usersReducer } from '../../containers/UsersPage/reducer';
import { selectSearchEmail, selectSort, selectUsers } from '../../containers/UsersPage/selectors';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import useIsAdminCompact from '../../hooks/useIsAdminCompact';
import { AdminLoading, AdminPagination, AdminTable } from '../components/V2AdminUI';
import V2Select from '../components/V2Select';
import './users.css';

const PAGE_SIZE = 20;
const subscriptions = [
  { label: '10–25', value: 1 },
  { label: '10–50', value: 2 },
  { label: '10–200', value: 3 },
  { label: 'Unlim', value: 4 },
];
const rooms = [
  { label: 'PS', value: 1 },
  { label: 'CHI', value: 2 },
  { label: 'WPN', value: 3 },
  { label: '888', value: 4 },
  { label: 'CPR', value: 5 },
  { label: 'IP', value: 6 },
];
const sortKeys = [
  'id',
  'email',
  'isConfirmed',
  'isAdmin',
  'last_search_date',
  'subscription',
  'subscription_date',
] as const;
type SortKey = (typeof sortKeys)[number];
const sortLabels: Record<SortKey, string> = {
  id: 'admin_users_id',
  email: 'admin_users_email',
  isConfirmed: 'admin_users_confirmed',
  isAdmin: 'admin_users_admin',
  last_search_date: 'admin_users_last_search',
  subscription: 'admin_users_subscription',
  subscription_date: 'admin_users_subscription_until',
};

const lastSearch = (user: UserDto) =>
  user.last_search_date ? dayjs(user.last_search_date).format('DD.MM.YY HH:mm') : '—';

const UserAccessFields: React.FC<{
  user: UserDto;
  field: 'subscription' | 'date' | 'rooms';
}> = ({ user, field }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  if (field === 'subscription') {
    return (
      <V2Select<number | ''>
        className="v2-admin-user-subscription"
        ariaLabel={t('admin_users_subscription_for', { email: user.email })}
        value={user.subscription ?? ''}
        options={[{ value: '', label: t('admin_users_subscription_none') }, ...subscriptions]}
        onChange={(value) =>
          dispatch(
            usersReducer.actions.changeUserSubscription({
              id: user.id,
              subscription: value === '' ? undefined : value,
            }),
          )
        }
      />
    );
  }

  if (field === 'date') {
    const value = user.subscription_date ?? user.subscriptionDate;
    return (
      <input
        className="v2-admin-input v2-admin-user-date"
        type="date"
        aria-label={t('admin_users_subscription_until_for', { email: user.email })}
        value={value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : ''}
        onChange={(event) =>
          dispatch(
            usersReducer.actions.changeUserSubscriptionDate({
              id: user.id,
              subscriptionDate: event.target.value || undefined,
            }),
          )
        }
      />
    );
  }

  const selected = user.available_pokersite_ids ?? user.availablePokersiteIds ?? [];
  return (
    <fieldset className="v2-admin-user-rooms">
      <legend className="v2-admin-users-sr-only">
        {t('admin_users_rooms_for', { email: user.email })}
      </legend>
      {rooms.map((room) => (
        <label className="v2-admin-user-room" key={room.value}>
          <input
            type="checkbox"
            className="v2-admin-checkbox"
            checked={selected.some((id) => id === room.value)}
            onChange={(event) =>
              dispatch(
                usersReducer.actions.changeUserAvailablePokersiteIds({
                  id: user.id,
                  availablePokersiteIds: event.target.checked
                    ? [...selected, room.value]
                    : selected.filter((id) => id !== room.value),
                }),
              )
            }
          />
          <span>{room.label}</span>
        </label>
      ))}
    </fieldset>
  );
};

const V2UsersList: React.FC = () => {
  useIsAdmin();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const compact = useIsAdminCompact();
  const users = useSelector(selectUsers);
  const searchEmail = useSelector(selectSearchEmail);
  const sort = useSelector(selectSort);
  const [page, setPage] = React.useState(1);
  const total = users?.length ?? 0;
  const activePage = Math.min(page, Math.max(1, Math.ceil(total / PAGE_SIZE)));
  const pageUsers = users?.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE) ?? [];

  React.useEffect(() => {
    dispatch(usersReducer.actions.loadUsers());
  }, [dispatch]);

  const updateSort = (key: SortKey, order?: string) => {
    const sortOrder =
      order ??
      (sort?.sortKey === key
        ? sort.sortOrder === 'ascend'
          ? 'descend'
          : sort.sortOrder === 'descend'
            ? ''
            : 'ascend'
        : 'ascend');
    dispatch(usersReducer.actions.changeTable({ sortKey: key, sortOrder }));
    dispatch(usersReducer.actions.loadUsers());
    setPage(1);
  };

  const sortTitle = (key: SortKey) => (
    <button
      type="button"
      className="v2-admin-users-sort"
      onClick={() => updateSort(key)}
      aria-label={`${t(sortLabels[key])}: ${t(
        sort?.sortKey === key && sort.sortOrder === 'descend'
          ? 'admin_users_sort_clear'
          : sort?.sortKey === key && sort.sortOrder === 'ascend'
            ? 'admin_users_sort_descending'
            : 'admin_users_sort_ascending',
      )}`}
    >
      {t(sortLabels[key])}{' '}
      <span aria-hidden="true">
        {sort?.sortKey === key && sort.sortOrder ? (sort.sortOrder === 'ascend' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
  const sortDirection = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    sort?.sortKey === key && sort.sortOrder
      ? sort.sortOrder === 'ascend'
        ? 'ascending'
        : 'descending'
      : 'none';
  const activate = (user: UserDto) =>
    !user.isConfirmed ? (
      <button
        type="button"
        className="acid-button v2-admin-user-activate"
        onClick={() => dispatch(usersReducer.actions.activateUser(user.id))}
      >
        {t('admin_users_activate')}
      </button>
    ) : null;
  const columns = [
    {
      key: 'id',
      title: sortTitle('id'),
      sort: sortDirection('id'),
      render: (user: UserDto) => user.id,
    },
    {
      key: 'email',
      title: sortTitle('email'),
      sort: sortDirection('email'),
      render: (user: UserDto) => (
        <Link className="v2-admin-user-link" to={`/users/${user.id}`}>
          {user.email}
        </Link>
      ),
    },
    {
      key: 'isConfirmed',
      title: sortTitle('isConfirmed'),
      sort: sortDirection('isConfirmed'),
      render: (user: UserDto) => (
        <span className={user.isConfirmed ? 'v2-admin-badge v2-admin-badge--ok' : 'v2-admin-badge'}>
          {t(user.isConfirmed ? 'admin_users_yes' : 'admin_users_no')}
        </span>
      ),
    },
    {
      key: 'isAdmin',
      title: sortTitle('isAdmin'),
      sort: sortDirection('isAdmin'),
      render: (user: UserDto) => (user.isAdmin ? t('admin_users_yes') : '—'),
    },
    {
      key: 'last_search_date',
      title: sortTitle('last_search_date'),
      sort: sortDirection('last_search_date'),
      render: lastSearch,
    },
    {
      key: 'subscription',
      title: sortTitle('subscription'),
      sort: sortDirection('subscription'),
      render: (user: UserDto) => <UserAccessFields user={user} field="subscription" />,
    },
    {
      key: 'subscription_date',
      title: sortTitle('subscription_date'),
      sort: sortDirection('subscription_date'),
      render: (user: UserDto) => <UserAccessFields user={user} field="date" />,
    },
    {
      key: 'rooms',
      title: t('admin_users_rooms'),
      render: (user: UserDto) => <UserAccessFields user={user} field="rooms" />,
    },
    { key: 'actions', title: '', render: activate },
  ];
  const pagination = { page: activePage, pageSize: PAGE_SIZE, total, onChange: setPage };

  return (
    <section className="settings-panel v2-admin-users-panel" aria-label={t('admin_users_list')}>
      <div className="settings-panel__head v2-admin-table-panel__head">
        <h2>{t('admin_users_list')}</h2>
        <p className="v2-admin-table-panel__meta" aria-live="polite">
          {t('admin_users_found', { count: total })}
        </p>
      </div>
      <form
        className="v2-admin-native-users-search"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          dispatch(usersReducer.actions.loadUsers());
        }}
      >
        <input
          className="v2-admin-input"
          type="search"
          aria-label={t('admin_users_search')}
          placeholder={t('admin_users_search')}
          value={searchEmail}
          onChange={(event) => dispatch(usersReducer.actions.changeEmail(event.target.value))}
        />
        <button className="acid-button" type="submit">
          {t('admin_users_search_submit')}
        </button>
      </form>
      {compact ? (
        <div className="v2-admin-users-mobile-sort">
          <label className="settings-field">
            <span className="settings-field-label">{t('admin_users_sort')}</span>
            <V2Select<SortKey | ''>
              ariaLabel={t('admin_users_sort')}
              value={sortKeys.find((key) => sort?.sortOrder && key === sort.sortKey) ?? ''}
              options={[
                { value: '', label: '—', disabled: true },
                ...sortKeys.map((key) => ({ value: key, label: t(sortLabels[key]) })),
              ]}
              onChange={(value) => {
                if (value) updateSort(value, 'ascend');
              }}
            />
          </label>
          <button
            type="button"
            className="outline-button"
            aria-label={t('admin_users_sort_direction')}
            disabled={!sort?.sortOrder}
            onClick={() =>
              updateSort(
                sort?.sortKey as SortKey,
                sort?.sortOrder === 'ascend' ? 'descend' : 'ascend',
              )
            }
          >
            {t(
              sort?.sortOrder === 'descend'
                ? 'admin_users_sort_descending'
                : 'admin_users_sort_ascending',
            )}
          </button>
        </div>
      ) : null}
      {!users ? (
        <AdminLoading />
      ) : compact ? (
        <>
          {pageUsers.length ? (
            <ul className="v2-admin-user-cards">
              {pageUsers.map((user) => (
                <li className="v2-admin-user-card" key={user.id}>
                  <div className="v2-admin-user-card__head">
                    <Link className="v2-admin-user-link" to={`/users/${user.id}`}>
                      {user.email}
                    </Link>
                    <span className="v2-admin-user-card__id">#{user.id}</span>
                  </div>
                  <div className="v2-admin-user-card__badges">
                    <span
                      className={
                        user.isConfirmed ? 'v2-admin-badge v2-admin-badge--ok' : 'v2-admin-badge'
                      }
                    >
                      {t(user.isConfirmed ? 'admin_users_confirmed' : 'admin_users_unconfirmed')}
                    </span>
                    {user.isAdmin ? (
                      <span className="v2-admin-badge v2-admin-badge--admin">
                        {t('admin_users_admin')}
                      </span>
                    ) : null}
                  </div>
                  <p className="v2-admin-user-card__meta">
                    {t('admin_users_last_search')}: {lastSearch(user)}
                  </p>
                  <div className="v2-admin-user-card__fields">
                    <label className="settings-field">
                      <span className="settings-field-label">{t('admin_users_subscription')}</span>
                      <UserAccessFields user={user} field="subscription" />
                    </label>
                    <label className="settings-field">
                      <span className="settings-field-label">
                        {t('admin_users_subscription_until')}
                      </span>
                      <UserAccessFields user={user} field="date" />
                    </label>
                    <div className="settings-field v2-admin-user-card__rooms">
                      <span className="settings-field-label">{t('admin_users_rooms')}</span>
                      <UserAccessFields user={user} field="rooms" />
                    </div>
                  </div>
                  {activate(user)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="table-hint">{t('admin_users_empty')}</p>
          )}
          <AdminPagination {...pagination} />
        </>
      ) : (
        <AdminTable
          columns={columns}
          rows={pageUsers}
          rowKey="id"
          emptyText={t('admin_users_empty')}
          minWidth={1180}
          caption={t('admin_users_list')}
          pagination={pagination}
        />
      )}
    </section>
  );
};

export default V2UsersList;
